import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Calendar, 
  DollarSign, 
  ShoppingBag, 
  MapPin,
  Clock,
  Package,
  CreditCard,
  CheckCircle,
  AlertCircle,
  XCircle,
  Hash,
  Printer,
  Share2,
  Edit,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order, Payment } from '../../services/dataService';

interface OrderDetailsProps {}

const OrderDetails: React.FC<OrderDetailsProps> = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { tenantData } = useAuth();
  
  const [order, setOrder] = useState<Order | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Helper function to safely convert date
  const safeToDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    if (typeof date === 'string' || typeof date === 'number') return new Date(date);
    return new Date();
  };

  // Helper function to safely format date
  const safeFormatDate = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleDateString();
    } catch (error) {
      return 'Unknown date';
    }
  };

  // Helper function to format date and time
  const formatDateTime = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleString();
    } catch (error) {
      return 'Unknown date';
    }
  };

  const loadOrderData = useCallback(async () => {
    if (!tenantData?.id || !orderId) return;

    try {
      setLoading(true);
      setError('');

      // Load order data
      const orderData = await dataService.getById<Order>(tenantData.id, 'orders', orderId);
      
      if (!orderData) {
        setError('Order not found');
        return;
      }



      setOrder(orderData);

      // Load payments for this order
      const allPayments = await dataService.getPayments(tenantData.id);
      const orderPayments = allPayments.filter(payment => payment.orderId === orderId);
      setPayments(orderPayments);

    } catch (error) {
      console.error('Error loading order details:', error);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [tenantData, orderId]);

  useEffect(() => {
    loadOrderData();
  }, [loadOrderData]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'active':
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Package className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getPaymentStatusColor = (order: Order) => {
    if (order.isPaid) return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
    if (order.paidAmount && order.paidAmount > 0) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
  };

  const getPaymentStatusText = (order: Order) => {
    if (order.isPaid) return 'Paid';
    if (order.paidAmount && order.paidAmount > 0) return 'Partial';
    return 'Unpaid';
  };

  const handleDelete = async () => {
    if (!tenantData?.id || !orderId) return;

    try {
      await dataService.delete(tenantData.id, 'orders', orderId);
      navigate('/order-history');
    } catch (error) {
      console.error('Error deleting order:', error);
      setError('Failed to delete order');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-lg p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/order-history')}
            className="btn-primary"
          >
            Back to Order History
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-lg p-8 text-center max-w-md">
          <Package className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Order Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The order you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/order-history')}
            className="btn-primary"
          >
            Back to Order History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 animate-slide-in-left">
            <button
              onClick={() => navigate('/order-history')}
              className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                #{order.id}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Order details and information
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 animate-slide-in-right">
            <button
              onClick={handlePrint}
              className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
              title="Print Order"
            >
              <Printer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
              title="Delete Order"
            >
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Order Status Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {/* Total Amount Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-0">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Total Amount</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">₹{(order.total || 0).toFixed(2)}</p>
          </div>

          {/* Order Date Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-100">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Order Date</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{safeFormatDate(order.createdAt)}</p>
          </div>

          {/* Items Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
              {/* Add-ons indicator */}
              {order.items && order.items.some(item => item.addOnSelections && item.addOnSelections.length > 0) && (
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-xs text-white font-bold">+</span>
                </div>
              )}
            </div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Items</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{order.items?.length || 0}</p>
            {/* Add-ons count */}
            {order.items && order.items.some(item => item.addOnSelections && item.addOnSelections.length > 0) && (
              <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                With Add-ons
              </p>
            )}
          </div>

          {/* Status Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                {getStatusIcon(order.status)}
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Status</p>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
          </div>
        </div>

        {/* Order Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Information */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{order.customer}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Customer Name</p>
                </div>
              </div>
              {order.customerPhone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{order.customerPhone}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Phone Number</p>
                  </div>
                </div>
              )}
              {order.tableNumber && (
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Table {order.tableNumber}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Table Number</p>
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(order.createdAt)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Order Time</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Status */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Status</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                {getStatusIcon(order.status)}
                <div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Order Status</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPaymentStatusColor(order)}`}>
                    {getPaymentStatusText(order)}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Payment Status</p>
                </div>
              </div>
              {(order.paidAmount || 0) > 0 && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Amount Paid</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                    ₹{(order.paidAmount || 0).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment History</h3>
            {payments.length > 0 ? (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">₹{payment.amount.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{payment.method}</p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {safeFormatDate(payment.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No payments recorded</p>
            )}
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Order Items</h3>
              {order.items && order.items.some(item => item.addOnSelections && item.addOnSelections.length > 0) && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">Add-ons Included</span>
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            {order.items && order.items.length > 0 ? (
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={item.id || index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {/* Main Item Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{item.menuItemName}</p>
                        {item.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.notes}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.quantity} × ₹{item.unitPrice?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          ₹{item.totalPrice?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Add-ons Breakdown */}
                    {item.addOnSelections && item.addOnSelections.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                        <div className="space-y-2">
                          {item.addOnSelections.map((addOnCategory, categoryIndex) => (
                            <div key={categoryIndex} className="ml-4">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                {addOnCategory.categoryName}:
                              </p>
                              <div className="space-y-1">
                                {addOnCategory.selectedOptions.map((option, optionIndex) => (
                                  <div key={optionIndex} className="flex items-center justify-between ml-3">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      • {option.optionName}
                                    </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-300">
                                      ₹{option.price.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add-ons Total */}
                        {item.addOnsTotal && item.addOnsTotal > 0 && (
                          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600 mt-3">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Add-ons Total:
                            </span>
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                              ₹{item.addOnsTotal.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Fallback: Check for any add-on related data */}
                    {(!item.addOnSelections || item.addOnSelections.length === 0) && 
                     (item.addOnsTotal && item.addOnsTotal > 0) && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Add-ons Cost:
                          </span>
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            ₹{item.addOnsTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Order Summary */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  {/* Subtotal */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">₹{(order.subtotal || 0).toFixed(2)}</p>
                  </div>
                  
                  {/* Add-ons Total */}
                  {order.items && order.items.some(item => item.addOnsTotal && item.addOnsTotal > 0) && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Add-ons Total:</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        ₹{order.items.reduce((sum, item) => sum + (item.addOnsTotal || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  )}
                  
                  {/* Tax */}
                  {order.tax && order.tax > 0 && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tax:</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">₹{order.tax.toFixed(2)}</p>
                    </div>
                  )}
                  
                  {/* Total */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">Total</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">₹{(order.total || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No items found</p>
            )}
            

          </div>
        </div>

        {/* Order Notes */}
        {order.notes && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Notes</h3>
            <p className="text-gray-700 dark:text-gray-300">{order.notes}</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-[90vw] max-w-md scale-95 animate-scale-in">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Delete Order</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete Order #{order.id}? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex-1"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrderDetails; 